import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";

import { useDisplays } from "@/hooks";
import { Display } from "@/utils";
import { useVenuesContext } from "@/contexts";

import { DashboardLayout } from "@/components/layouts/DashboardLayout";

import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import {
  DisplayFormModal,
  DisplaysTable,
  NeedAddVenue,
  VenueSelector,
} from "@/components/features";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const DisplaysPage: React.FC<PageProps> = (props: PageProps) => {
  const navigate = useNavigate();
  const {
    loading: venuesLoading,
    selectedVenue,
    setSelectedVenue,
    hasVenues,
  } = useVenuesContext();

  // Local search state
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Use the displays hook for all data management
  const {
    // Data state
    displays,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    filters,
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingDisplayId,
    selectedDisplay,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // UI state management
    setSelectedDisplay,

    // Error handling
    lastError,
    clearError,

    // Helper operations
    reset,
  } = useDisplays({
    venueId: selectedVenue?.id || "",
    autoLoad: false, // We'll manually load when venue is selected
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [displayToDelete, setDisplayToDelete] = useState<Display | null>(null);

  // Effect to load displays when venue changes
  useEffect(() => {
    if (selectedVenue?.id) {
      // Reset any existing data and load for new venue
      reset();
      const venueFilter = { venueId: [selectedVenue.id] };
      list(venueFilter, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    } else {
      // Clear displays when no venue selected
      reset();
    }
  }, [selectedVenue?.id, reset, list, sortBy]);

  // Handle venue selection change
  const handleVenueChange = useCallback(
    (venueId: string | null) => {
      if (venueId && selectedVenue?.id !== venueId) {
        // Find and set the venue object - this is a simplified approach
        // In a real app, you might want to fetch the venue object by ID
        // For now, we'll just update with the ID and let the context handle it
        setSelectedVenue({ id: venueId } as any);
      } else if (!venueId) {
        setSelectedVenue(null);
      }
    },
    [selectedVenue?.id, setSelectedVenue]
  );

  // Handle search with debouncing
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchKeyword(value);

      if (!selectedVenue?.id) return;

      // Apply search filter
      const newFilters = { ...filters };
      if (value.trim()) {
        newFilters.name = value.trim();
      } else {
        delete newFilters.name;
      }

      // Ensure venueId filter is maintained
      newFilters.venueId = [selectedVenue.id];

      // Update filters and reload data
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: 20 }, sortBy, true);
    },
    [selectedVenue?.id, filters, setFilters, list, sortBy]
  );

  // Handle add display button click
  const handleAddDisplay = useCallback(() => {
    setSelectedDisplay(null);
    setFormModalOpened(true);
  }, [setSelectedDisplay]);

  // Handle edit display
  const handleEditDisplay = useCallback(
    (display: Display) => {
      setSelectedDisplay(display);
      setFormModalOpened(true);
    },
    [setSelectedDisplay]
  );

  const handleDuplicateDisplay = useCallback(
    (display: Display) => {
      setSelectedDisplay({ ...display, id: "" });
      setFormModalOpened(true);
    },
    [setSelectedDisplay]
  );

  // Handle view display (could navigate to display details page)
  const handleViewDisplay = useCallback((display: Display) => {
    // TODO: Navigate to display details page or show details modal
    window.open(`/displays/${display.id}/preview`);
  }, []);

  const handleGoInDisplay = useCallback(
    (display: Display) => {
      // TODO: Navigate to display details page or show details modal
      navigate(`/displays/${display.id}/tvs`);
    },
    [navigate]
  );

  // Handle remove display button click
  const handleRemoveDisplayClick = useCallback((display: Display) => {
    setDisplayToDelete(display);
    setDeleteModalOpened(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (displayToDelete?.id) {
      const success = await remove(displayToDelete.id);
      if (success) {
        setDeleteModalOpened(false);
        setDisplayToDelete(null);
      }
      // Error handling is managed by the hook
    }
  }, [displayToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setDisplayToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedDisplay(null);
    clearError();
  }, [setSelectedDisplay, clearError]);

  // Handle export (placeholder)
  /* const handleExport = useCallback(() => {
    // TODO: Implement export functionality
    console.log("Export displays");
  }, []); */

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (selectedVenue?.id) {
      refresh();
    }
  }, [selectedVenue?.id, refresh]);

  // Handle display creation
  const handleCreateDisplay = useCallback(
    async (displayData: any) => {
      const result = await create(displayData);
      if (result) {
        setFormModalOpened(false);
        setSelectedDisplay(null);
      }
      return result;
    },
    [create, setSelectedDisplay]
  );

  // Handle display update
  const handleUpdateDisplay = useCallback(
    async (displayId: string, displayData: any) => {
      const result = await update(displayId, displayData);
      if (result) {
        setFormModalOpened(false);
        setSelectedDisplay(null);
      }
      return result;
    },
    [update, setSelectedDisplay]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with venue selector, actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "venue-selector",
              items: [
                <VenueSelector
                  key="venue-selector"
                  value={selectedVenue?.id || null}
                  onChange={handleVenueChange}
                  placeholder="Select venue to manage displays"
                  style={{ minWidth: 250 }}
                  required
                />,
                <AppButton
                  key="add-display"
                  iconName="Plus"
                  onClick={handleAddDisplay}
                  disabled={isCreating || loading || !selectedVenue?.id}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={loading || !selectedVenue?.id}
                >
                  Refresh
                </AppButton>,
                /*<AppButton
                  key="export"
                  variant="secondary"
                  iconName="Download"
                  onClick={handleExport}
                  disabled={isEmpty || !selectedVenue?.id}
                >
                  Export
                </AppButton>,*/
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search displays..."
                  value={searchKeyword}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                  disabled={!selectedVenue?.id}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Displays table - only show when venue is selected */}
        {selectedVenue?.id && (
          <DisplaysTable
            displays={displays}
            loading={loading || venuesLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onLoadMore={loadMore}
            onEditDisplay={handleEditDisplay}
            onDuplicateDisplay={handleDuplicateDisplay}
            onRemoveDisplay={handleRemoveDisplayClick}
            onViewDisplay={handleViewDisplay}
            onGoInDisplay={handleGoInDisplay}
            removingDisplayId={removingDisplayId}
            size="md"
          />
        )}

        {/* Empty state when venue selected but no displays */}
        <EmptyState
          display={!!selectedVenue?.id && isEmpty && !loading && !venuesLoading}
          title="No displays yet"
          description="Start by adding your first display to this venue"
          actionIcon="Plus"
          actionLabel="Add Display"
          onAction={handleAddDisplay}
        />

        {/* Empty state for no venue exist */}
        <NeedAddVenue
          display={!hasVenues && !venuesLoading && !loading}
          descriptionExtra="Then add rooms in it and then you can start adding recurring events"
        />

        {/* Venue Selection Message */}
        <EmptyState
          display={!selectedVenue?.id && hasVenues && !venuesLoading}
          title="Select a venue"
          description="Choose a venue from the dropdown above to view and manage its displays"
        />
      </Stack>

      {/* Display form modal for create/edit */}
      <DisplayFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        display={selectedDisplay}
        onCreateDisplay={handleCreateDisplay}
        onUpdateDisplay={handleUpdateDisplay}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
        venueId={selectedVenue?.id || ""}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Display"
        message={`Are you sure you want to delete "${displayToDelete?.name}"?`}
        details="This action cannot be undone. The display configuration will be permanently removed."
        confirmInProgress={removingDisplayId === displayToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
